import {type ComponentProps} from 'react';
import {Bar, BarChart, CartesianGrid, ReferenceLine, XAxis, YAxis} from 'recharts';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent
} from '@kroll-resolver-crisp/ui-components-react';
import {type DeliveryGranularity, type PolicyViolationDeliveryCount} from '../../api/submissions';
import {targetDelta} from '../../lib/targetDelta';
import {VERTICAL_GRID} from './verticalGrid';

// Backend buckets sent-at by the UTC calendar for every analyst (GetDailyStats), so that everyone's
// deliveries land in the same day/week/month (GT-16894). Iterate and label in UTC here too: local
// time would build slots whose keys don't match the API's and draw a bar a day off near midnight.
const DAY_LABEL = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC'
});
const MONTH_LABEL = new Intl.DateTimeFormat('en-US', {month: 'short', timeZone: 'UTC'});

// How many buckets each granularity shows, and what the request asks the API for. A year of months
// and a quarter of weeks each cover enough ground to read a trend without the bars going hairline.
export const BUCKET_COUNT: Record<DeliveryGranularity, number> = {
  day: 30,
  week: 12,
  month: 12
};

// The design system's chart-1..5 is one sequential ramp, which reads fine for up to five verticals
// side by side; past that, adjacent steps become indistinguishable, so the app-local categorical
// palette takes over. Both are tokens, so each flips with the theme.
const SEQUENTIAL_SERIES_LIMIT = 5;
const CATEGORICAL_SERIES_COUNT = 8;

// The shortfall row in the tooltip has no bar of its own, so its swatch is a faint ink rather than
// a palette colour that could be mistaken for another vertical's.
const SHORTFALL_COLOR = 'color-mix(in oklab, var(--foreground) 18%, transparent)';

// Failed deliveries stack on the sent bar in a muted ink: they need to be seen without reading as an
// alarm or competing with the vertical's own colour.
const FAILED_COLOR = 'color-mix(in oklab, var(--foreground) 35%, transparent)';

function utcIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function utcDate(year: number, monthIndex: number, day: number): Date {
  return new Date(Date.UTC(year, monthIndex, day));
}

// The first UTC day of the bucket `date` falls in - mirrors SubmissionsController.BucketStart, so
// the slots built here line up with the keys the API returns. Monday-start weeks, calendar months.
export function bucketStart(date: Date, granularity: DeliveryGranularity): Date {
  if (granularity === 'week') {
    return utcDate(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate() - ((date.getUTCDay() + 6) % 7)
    );
  }

  if (granularity === 'month') {
    return utcDate(date.getUTCFullYear(), date.getUTCMonth(), 1);
  }

  return utcDate(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

// Walk `steps` buckets back from `from`, in that granularity's own unit. Going back in months via
// setMonth rather than days is what keeps a 12-month window on the 1st of each month regardless of
// how long those months are.
export function shiftBuckets(from: Date, granularity: DeliveryGranularity, steps: number): Date {
  if (granularity === 'week') {
    return utcDate(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate() - 7 * steps);
  }

  if (granularity === 'month') {
    return utcDate(from.getUTCFullYear(), from.getUTCMonth() - steps, 1);
  }

  return utcDate(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate() - steps);
}

// Recharts reads each series off a row property and the chart container publishes each series'
// colour as `--color-<key>`, so a policy violation name ("Suicide & Self Harm") can't be the key
// directly. Series are keyed by position instead.
function seriesKey(index: number): string {
  return `s${index}`;
}

function seriesColor(index: number, seriesCount: number): string {
  if (seriesCount <= SEQUENTIAL_SERIES_LIMIT) {
    return `var(--chart-${index + 1})`;
  }

  return `var(--chart-cat-${(index % CATEGORICAL_SERIES_COUNT) + 1})`;
}

// The failed-delivery series sits on the same row as its sent series, under its own key.
function failedKey(index: number): string {
  return `f${index}`;
}

type ChartRow = {date: string; label: string} & Record<string, number | string>;

// One row per bucket in the window, each carrying a count per series. `seriesOf` names the series a
// delivery row belongs to; rows it maps to no series are dropped, and rows sharing a bucket and a
// series are summed - the API splits each bucket by policy violation *and* risk, and a chart per
// violation rolls the risks back up.
export function buildRows(
  rows: PolicyViolationDeliveryCount[],
  series: string[],
  seriesOf: (row: PolicyViolationDeliveryCount) => string | undefined,
  granularity: DeliveryGranularity
): ChartRow[] {
  const keyBySeries = new Map(series.map((name, i) => [name, seriesKey(i)] as const));
  const countByDateAndSeries = new Map<string, number>();
  const failedByDateAndSeries = new Map<string, number>();
  for (const row of rows) {
    const name = seriesOf(row);
    const key = name === undefined ? undefined : keyBySeries.get(name);
    if (key !== undefined) {
      const slot = `${row.date}|${key}`;
      countByDateAndSeries.set(slot, (countByDateAndSeries.get(slot) ?? 0) + row.count);
      failedByDateAndSeries.set(slot, (failedByDateAndSeries.get(slot) ?? 0) + row.failed);
    }
  }

  const current = bucketStart(new Date(), granularity);
  const count = BUCKET_COUNT[granularity];
  const label = granularity === 'month' ? MONTH_LABEL : DAY_LABEL;

  // Every slot is emitted even when the API returned no row for it, so a quiet week still draws a
  // zero bar rather than silently narrowing the window.
  return Array.from({length: count}, (_, i) => {
    const bucket = shiftBuckets(current, granularity, count - 1 - i);
    const date = utcIsoDate(bucket);
    const point: ChartRow = {date, label: label.format(bucket)};
    for (const [position, key] of [...keyBySeries.values()].entries()) {
      point[key] = countByDateAndSeries.get(`${date}|${key}`) ?? 0;
      point[failedKey(position)] = failedByDateAndSeries.get(`${date}|${key}`) ?? 0;
    }

    return point;
  });
}

// Recharts clones this with the reference line's viewBox. It sits just above the line at its right
// end, haloed in the card colour so a bar running through it doesn't swallow the text.
function TargetLabel({
  viewBox,
  target
}: {
  readonly viewBox?: {x?: number; y?: number; width?: number};
  readonly target: number;
}) {
  const x = (viewBox?.x ?? 0) + (viewBox?.width ?? 0);
  const y = (viewBox?.y ?? 0) - 4;
  return (
    <text
      x={x}
      y={y}
      textAnchor="end"
      fontSize={10}
      letterSpacing="0.06em"
      fill="var(--foreground)"
      stroke="var(--card)"
      strokeWidth={3}
      paintOrder="stroke">
      TARGET {target.toLocaleString()}
    </text>
  );
}

type TooltipPayload = NonNullable<ComponentProps<typeof ChartTooltipContent>['payload']>;

// The tooltip's rows: the sent count, then - when something failed - the failed count and the total
// submitted, then the surplus over the target or the shortfall still to send. Failed deliveries never
// count toward the target, so the surplus/shortfall is measured on the sent count alone. With nothing
// failed and no target the payload passes through unchanged.
export function withTargetDelta(payload: TooltipPayload, target: number): TooltipPayload {
  const [sent, ...rest] = payload;
  if (!sent || typeof sent.value !== 'number') {
    return payload;
  }

  const failed = rest.filter((row) => typeof row.value === 'number' && row.value !== 0);
  const failedCount = failed.reduce((sum, row) => sum + Number(row.value), 0);
  const total: TooltipPayload =
    failedCount > 0
      ? [
          {
            ...sent,
            dataKey: 'total',
            name: 'Total',
            value: sent.value + failedCount,
            color: 'var(--foreground)'
          }
        ]
      : [];
  const delta = target > 0 ? targetDelta(sent.value, target) : undefined;
  const deltaRow: TooltipPayload = delta
    ? [
        {
          ...sent,
          dataKey: 'delta',
          name: delta.label,
          value: delta.value,
          color: delta.label === 'Shortfall' ? SHORTFALL_COLOR : 'var(--foreground)'
        }
      ]
    : [];

  return [sent, ...failed, ...total, ...deltaRow];
}

// Recharts passes its own props through `content`, so this rewrites the payload and hands
// everything else to the design system's tooltip untouched.
function SentTooltip({
  target,
  payload,
  ...props
}: ComponentProps<typeof ChartTooltipContent> & {readonly target: number}) {
  return (
    <ChartTooltipContent
      {...props}
      {...(payload ? {payload: withTargetDelta(payload, target)} : {})}
    />
  );
}

function VerticalChart({
  rows,
  index,
  seriesCount,
  target,
  title,
  description
}: {
  readonly rows: ChartRow[];
  readonly index: number;
  readonly seriesCount: number;
  readonly target: number;
  readonly title: string;
  readonly description: string;
}) {
  const key = seriesKey(0);
  const failed = failedKey(0);
  const config: ChartConfig = {
    [key]: {label: 'Sent', color: seriesColor(index, seriesCount)},
    [failed]: {label: 'Delivery failed', color: FAILED_COLOR}
  };

  return (
    <Card size="sm" className="min-w-0">
      <CardHeader>
        <CardTitle className="truncate">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={config} className="aspect-auto h-40 w-full">
          <BarChart accessibilityLayer data={rows} margin={{top: 12, right: 8, bottom: 0, left: 0}}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              interval={Math.ceil(rows.length / 4) - 1}
            />
            <YAxis tickLine={false} axisLine={false} allowDecimals={false} width={32} />
            <ChartTooltip content={<SentTooltip target={target} />} />
            <Bar dataKey={key} stackId="delivery" fill={`var(--color-${key})`} maxBarSize={28} />
            <Bar
              dataKey={failed}
              stackId="delivery"
              fill={`var(--color-${failed})`}
              maxBarSize={28}
            />
            {/* extendDomain keeps the line on the chart when every bar falls short of it, which is
                exactly when it matters most. */}
            {target > 0 ? (
              <ReferenceLine
                y={target}
                ifOverflow="extendDomain"
                stroke="var(--foreground)"
                strokeWidth={2}
                strokeDasharray="2 3"
                label={<TargetLabel target={target} />}
              />
            ) : null}
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

// What was sent to clients, one small chart per policy violation, each against that violation's own
// target for the period in view. One chart each rather than one stacked chart: a target is a
// per-violation number, and a reference line per series on a stack has no honest baseline to sit
// against. A violation whose hopper has no target for the period draws its bars without a line.
export function DeliveryCharts({
  data,
  policyViolations,
  targets,
  granularity,
  submissionType
}: {
  readonly data: PolicyViolationDeliveryCount[];
  readonly policyViolations: string[];
  readonly targets: Readonly<Record<string, number>>;
  readonly granularity: DeliveryGranularity;
  readonly submissionType: string;
}) {
  return (
    <div className={VERTICAL_GRID}>
      {policyViolations.map((violation, index) => (
        <VerticalChart
          key={violation}
          title={violation}
          description={`Sent to client per ${granularity} (failed deliveries in grey) · ${submissionType}`}
          index={index}
          seriesCount={policyViolations.length}
          target={targets[violation] ?? 0}
          rows={buildRows(data, [violation], (row) => row.policy_violation, granularity)}
        />
      ))}
    </div>
  );
}
